<script>
export default {
  name: 'myProfile',
  data() {
    return {
      activeTab: 'basic',
      profile: {},
      editMode: false,
      editForm: { name: '', gender: 0 },
      saving: false,
      loginLogs: [],
      loginLogsTotal: 0,
      loginLogsPage: 1,
      loginLogsSize: 20,
      loginLogsLoading: false,
      devices: [],
      devicesLoading: false,
    }
  },
  computed: {
    genderLabel() {
      const map = { 0: '未知', 1: '男', 2: '女' }
      return map[this.profile.gender] || '未知'
    },
    genderTagType() {
      const map = { 0: 'info', 1: 'primary', 2: 'danger' }
      return map[this.profile.gender] || 'info'
    }
  },
  mounted() {
    this.fetchProfile()
  },
  methods: {
    async fetchProfile() {
      const { code, data } = await this.$axios.get('/api/v1/manage/admin/my/profile')
      if (code === 1 && data) {
        this.profile = data
      }
    },
    startEdit() {
      this.editForm = { name: this.profile.name || '', gender: this.profile.gender || 0 }
      this.editMode = true
    },
    cancelEdit() {
      this.editMode = false
    },
    async saveProfile() {
      this.saving = true
      const { code } = await this.$axios.post('/api/v1/manage/admin/my/profile', {
        name: this.editForm.name,
        gender: this.editForm.gender
      })
      this.saving = false
      if (code === 1) {
        this.$message.success('保存成功')
        this.editMode = false
        this.fetchProfile()
      }
    },
    async fetchLoginLogs() {
      this.loginLogsLoading = true
      const { code, data, total } = await this.$axios.get('/api/v1/manage/admin/login/logs', {
        adminId: this.mixinUserInfo.id,
        appKey: this.mixinUserInfo.appKey,
        page: this.loginLogsPage,
        size: this.loginLogsSize
      })
      if (code === 1) {
        this.loginLogs = data || []
        this.loginLogsTotal = total || 0
      }
      this.loginLogsLoading = false
    },
    async fetchDevices() {
      this.devicesLoading = true
      const { code, data } = await this.$axios.get('/api/v1/manage/admin/bound/devices')
      if (code === 1) {
        this.devices = data || []
      }
      this.devicesLoading = false
    },
    async untrustDevice(deviceId) {
      await this.$confirm('确认解绑该设备？解绑后该设备需重新验证才能登录。', '提示')
      const { code } = await this.$axios.post('/api/v1/manage/admin/bound/devices/untrust', { deviceId })
      if (code === 1) {
        this.$message.success('解绑成功')
        this.fetchDevices()
      }
    },
    handleTabClick(tab) {
      if (tab.name === 'login') {
        this.fetchLoginLogs()
      } else if (tab.name === 'security') {
        this.fetchDevices()
      }
    },
    handleLoginLogsPageChange(page) {
      this.loginLogsPage = page
      this.fetchLoginLogs()
    },
    formatTs(ts) {
      if (!ts) return '-'
      return this.$options.filters.filterTime(ts)
    },
    isCurrentDevice(fp) {
      return false
    },
    triggerProfileAvatarUpload() {
      this.$refs.profileAvatarInput.click()
    },
    async handleProfileAvatarChange(e) {
      const file = e.target.files[0]
      if (!file) return
      e.target.value = ''
      if (file.size > 5 * 1024 * 1024) {
        this.$message.warning('图片不能超过5MB')
        return
      }
      const token = await this.getQiniuTokenFn()
      if (!token) { this.$message.error('获取上传凭证失败'); return }
      const key = `admin/avatar/${Date.now()}_${Math.random().toString(36).slice(2)}.${file.name.split('.').pop()}`
      const formData = new FormData()
      formData.append('file', file)
      formData.append('token', token)
      formData.append('key', key)
      const loading = this.$loading({ text: '上传中...' })
      try {
        const res = await fetch('https://up-z1.qiniup.com', { method: 'POST', body: formData })
        const json = await res.json()
        if (json && json.key) {
          const { code } = await this.$axios.post('/api/v1/manage/admin/my/avatar', { avatar: json.key })
          if (code === 1) {
            this.$set(this.profile, 'avatar', json.key)
            this.$message.success('头像更新成功')
          }
        } else {
          this.$message.error('上传失败')
        }
      } catch (err) {
        this.$message.error('上传出错')
      } finally {
        loading.close()
      }
    }
  }
}
</script>

<template>
  <div class="myProfile-page">
    <div class="profile-header">
      <div class="avatar-wrap">
        <div class="avatar-circle" :class="{ clickable: editMode }" @click="editMode && triggerProfileAvatarUpload()">
          <img v-if="profile.avatar" :src="mixinQiniuImg(profile.avatar)" class="avatar-img" @error="profile.avatar = null" />
          <span class="avatar-letter" v-else>{{ (profile.name || '?').charAt(0) }}</span>
          <div class="avatar-upload-mask" v-if="editMode">
            <i class="el-icon-camera"></i>
          </div>
        </div>
        <input ref="profileAvatarInput" type="file" accept="image/jpeg,image/png" style="display:none" @change="handleProfileAvatarChange" />
      </div>
      <div class="header-info">
        <div class="header-name">{{ profile.name || '-' }}</div>
        <div class="header-sub">
          <el-tag size="mini" :type="genderTagType" style="margin-right:8px">{{ genderLabel }}</el-tag>
          <el-tag size="mini" type="info">{{ profile.roleName || '未知角色' }}</el-tag>
          <el-tag size="mini" :type="profile.status === 1 ? 'success' : 'danger'" style="margin-left:8px">
            {{ profile.status === 1 ? '正常' : '已禁用' }}
          </el-tag>
        </div>
      </div>
    </div>

    <el-tabs v-model="activeTab" @tab-click="handleTabClick" class="profile-tabs">
      <!-- Tab1: 基本信息 -->
      <el-tab-pane label="基本信息" name="basic">
        <div class="tab-card">
          <div class="card-header">
            <span>账号基本信息</span>
            <el-button v-if="!editMode" size="small" type="primary" plain @click="startEdit">编辑</el-button>
            <span v-else>
              <el-button size="small" @click="cancelEdit">取消</el-button>
              <el-button size="small" type="primary" :loading="saving" @click="saveProfile">保存</el-button>
            </span>
          </div>

          <el-form v-if="!editMode" label-width="110px" class="info-form">
            <el-form-item label="手机号">
              <span class="info-value">{{ profile.phone || '-' }}</span>
            </el-form-item>
            <el-form-item label="姓名">
              <span class="info-value">{{ profile.name || '-' }}</span>
            </el-form-item>
            <el-form-item label="性别">
              <el-tag size="small" :type="genderTagType">{{ genderLabel }}</el-tag>
            </el-form-item>
            <el-form-item label="角色权限">
              <span class="info-value">{{ profile.roleName || '-' }}</span>
            </el-form-item>
            <el-form-item label="登录次数">
              <span class="info-value highlight">{{ profile.loginCount || 0 }} 次</span>
            </el-form-item>
            <el-form-item label="最后登录时间">
              <span class="info-value">{{ formatTs(profile.lastLoginTime) }}</span>
            </el-form-item>
            <el-form-item label="最后登录IP">
              <span class="info-value">{{ profile.lastLoginIp || '-' }}</span>
            </el-form-item>
            <el-form-item label="最后登录位置">
              <span class="info-value">{{ profile.lastLoginAddress || '-' }}</span>
            </el-form-item>
            <el-form-item label="最后登录设备">
              <span class="info-value">{{ profile.lastLoginDevice || '-' }}</span>
            </el-form-item>
          </el-form>

          <el-form v-else label-width="110px" class="info-form">
            <el-form-item label="姓名">
              <el-input v-model="editForm.name" size="small" style="width:220px" maxlength="20" placeholder="请输入真实姓名"></el-input>
            </el-form-item>
            <el-form-item label="性别">
              <el-radio-group v-model="editForm.gender" size="small">
                <el-radio :label="0">未知</el-radio>
                <el-radio :label="1">男</el-radio>
                <el-radio :label="2">女</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-form>
        </div>
      </el-tab-pane>

      <!-- Tab2: 登录信息 -->
      <el-tab-pane label="登录信息" name="login">
        <div class="tab-card">
          <div class="card-header">
            <span>最近登录记录</span>
            <el-button size="small" icon="el-icon-refresh" @click="fetchLoginLogs">刷新</el-button>
          </div>
          <el-table :data="loginLogs" v-loading="loginLogsLoading" size="small" stripe border>
            <el-table-column label="登录时间" min-width="150">
              <template slot-scope="{ row }">{{ formatTs(row.created) }}</template>
            </el-table-column>
            <el-table-column label="IP地址" prop="ip" min-width="130"></el-table-column>
            <el-table-column label="登录位置" min-width="180">
              <template slot-scope="{ row }">
                <span>{{ row.ipAddress || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="浏览器/系统" min-width="200">
              <template slot-scope="{ row }">
                <div>{{ row.browser || '-' }}</div>
                <div style="color:#999;font-size:12px">{{ row.os || '' }}</div>
              </template>
            </el-table-column>
            <el-table-column label="网络/设备" min-width="160">
              <template slot-scope="{ row }">
                <div>
                  <el-tag v-if="row.networkType" size="mini" type="info" style="margin-right:4px">{{ row.networkType }}</el-tag>
                  <el-tag v-if="row.deviceType" size="mini">{{ row.deviceType }}</el-tag>
                </div>
                <div style="color:#999;font-size:12px">{{ row.deviceBrand }} {{ row.deviceModel }}</div>
              </template>
            </el-table-column>
            <el-table-column label="运营商" prop="isp" min-width="100"></el-table-column>
            <el-table-column label="是否代理" min-width="90">
              <template slot-scope="{ row }">
                <el-tag v-if="row.isProxy" type="warning" size="mini">代理</el-tag>
                <el-tag v-else type="success" size="mini">正常</el-tag>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination style="float:right;margin-top:12px"
            @current-change="handleLoginLogsPageChange"
            :current-page="loginLogsPage"
            :page-size="loginLogsSize"
            layout="total, prev, pager, next"
            :total="loginLogsTotal">
          </el-pagination>
        </div>
      </el-tab-pane>

      <!-- Tab3: 安全中心 -->
      <el-tab-pane label="安全中心" name="security">
        <div class="tab-card">
          <div class="card-header">
            <span>已绑定设备</span>
            <el-button size="small" icon="el-icon-refresh" @click="fetchDevices">刷新</el-button>
          </div>
          <div class="security-tip">
            <i class="el-icon-info" style="color:#409eff;margin-right:6px"></i>
            每次从新设备登录时，设备信息将自动记录。您可在此解绑不再使用的设备。
          </div>
          <el-table :data="devices" v-loading="devicesLoading" size="small" stripe border>
            <el-table-column label="设备指纹" min-width="120">
              <template slot-scope="{ row }">
                <span class="fingerprint">{{ row.deviceFingerprintMasked || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="浏览器/系统" min-width="200">
              <template slot-scope="{ row }">
                <div>{{ row.browser || '-' }}</div>
                <div style="color:#999;font-size:12px">{{ row.os || '' }}</div>
              </template>
            </el-table-column>
            <el-table-column label="设备信息" min-width="160">
              <template slot-scope="{ row }">
                <div>
                  <el-tag v-if="row.deviceType" size="mini">{{ row.deviceType }}</el-tag>
                </div>
                <div style="color:#999;font-size:12px">{{ row.deviceBrand }} {{ row.deviceModel }}</div>
              </template>
            </el-table-column>
            <el-table-column label="首次绑定时间" min-width="150">
              <template slot-scope="{ row }">{{ formatTs(row.firstLoginTime) }}</template>
            </el-table-column>
            <el-table-column label="最后使用时间" min-width="150">
              <template slot-scope="{ row }">{{ formatTs(row.lastLoginTime) }}</template>
            </el-table-column>
            <el-table-column label="IP" prop="firstLoginIp" min-width="120"></el-table-column>
            <el-table-column label="状态" min-width="80">
              <template slot-scope="{ row }">
                <el-tag :type="row.isTrusted === 1 ? 'success' : 'info'" size="mini">
                  {{ row.isTrusted === 1 ? '已绑定' : '已解绑' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="90" fixed="right">
              <template slot-scope="{ row }">
                <el-button v-if="row.isTrusted === 1" type="text" style="color:#f56c6c" @click="untrustDevice(row.id)">解绑</el-button>
                <span v-else style="color:#ccc">已解绑</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<style scoped lang="less">
.myProfile-page {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100%;

  .profile-header {
    display: flex;
    align-items: center;
    background: #fff;
    border-radius: 10px;
    padding: 24px 28px;
    margin-bottom: 20px;
    box-shadow: 0 1px 6px rgba(0,0,0,0.06);

    .avatar-wrap {
      margin-right: 20px;

      .avatar-circle {
        position: relative;
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: linear-gradient(135deg, #409eff, #67c23a);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;

        &.clickable { cursor: pointer; }

        .avatar-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .avatar-letter {
          color: #fff;
          font-size: 28px;
          font-weight: 600;
        }

        .avatar-upload-mask {
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s;
          i { color: #fff; font-size: 20px; }
        }

        &.clickable:hover .avatar-upload-mask { opacity: 1; }
      }
    }

    .header-info {
      .header-name {
        font-size: 20px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 8px;
      }

      .header-sub {
        display: flex;
        align-items: center;
      }
    }
  }

  .profile-tabs {
    background: transparent;
  }

  .tab-card {
    background: #fff;
    border-radius: 10px;
    padding: 20px 24px;
    box-shadow: 0 1px 6px rgba(0,0,0,0.06);

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;
      padding-bottom: 14px;
      border-bottom: 1px solid #ebeef5;
      font-size: 15px;
      font-weight: 600;
      color: #303133;
    }
  }

  .info-form {
    .info-value {
      color: #606266;
      font-size: 14px;

      &.highlight {
        color: #409eff;
        font-weight: 600;
        font-size: 16px;
      }
    }
  }

  .security-tip {
    background: #f0f9ff;
    border: 1px solid #bae0ff;
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 16px;
    font-size: 13px;
    color: #606266;
  }

  .fingerprint {
    font-family: monospace;
    font-size: 12px;
    color: #909399;
  }
}
</style>
